import { defineChatTool } from "@codenexus/react";
import type { ChatTool } from "@codenexus/react";
import { deleteAzureDevOpsCredentials } from "@/api/integrations/azureDevOps";
import { deleteAtlassianCredentials } from "@/api/integrations/atlassian";
import { disconnectGitProvider } from "@/api/integrations/links";
import type { AppChatEventMap } from "../types";

export type IntegrationDisconnectProvider = "azure_devops" | "jira" | "github" | "gitlab" | "bitbucket";
export type DisconnectStateStore = undefined;

const PROVIDER_NAMES: Record<IntegrationDisconnectProvider, string> = {
    azure_devops: "Azure DevOps",
    jira: "Jira",
    github: "GitHub",
    gitlab: "GitLab",
    bitbucket: "Bitbucket"
};

// Kept temporarily to avoid changing createChatTools' positional API. Immediate
// disconnects have no renderer state to preserve.
export function createDisconnectStateStore(): undefined {
    return undefined;
}

export function createIntegrationDisconnectTool(
    getToken: () => Promise<string>,
    _stateStore?: DisconnectStateStore
): ChatTool {
    return defineChatTool<{ provider: IntegrationDisconnectProvider }, AppChatEventMap>({
        name: "integration_disconnect",
        category: "mutation",
        description:
            "Immediately disconnects an integration when the user asks to disconnect, remove, unlink, or revoke it. " +
            "This action does not require a separate confirmation. Use it for Azure DevOps/Azure Repos, Jira, GitHub, " +
            "GitLab, and Bitbucket. Do not use navigate_to_integrations for a disconnect request. Report the returned " +
            "outcome to the user without exposing raw HTTP status codes or internal error details.",
        parameters: {
            type: "object",
            properties: {
                provider: {
                    type: "string",
                    enum: ["azure_devops", "jira", "github", "gitlab", "bitbucket"],
                    description: "The integration to disconnect. Use azure_devops for Azure DevOps or Azure Repos."
                }
            },
            required: ["provider"],
            additionalProperties: false
        },
        execute: async ({ provider }, ctx) => {
            const name = PROVIDER_NAMES[provider];
            try {
                const token = await getToken();
                if (provider === "azure_devops" || provider === "jira") {
                    const response = provider === "azure_devops"
                        ? await deleteAzureDevOpsCredentials(token)
                        : await deleteAtlassianCredentials(token);
                    if (!response.success) throw new Error("Disconnect rejected");
                    const alreadyDisconnected = response.message === "No active credentials found";
                    ctx?.emit("integration_connection_changed", {
                        provider: provider === "azure_devops" ? "azure-devops" : provider
                    });
                    return {
                        ok: true,
                        provider,
                        outcome: alreadyDisconnected ? "already_disconnected" : "disconnected",
                        message: alreadyDisconnected ? `${name} was already disconnected.` : `${name} disconnected successfully.`
                    };
                }

                const response = await disconnectGitProvider(provider, token);
                if (!response.success) throw new Error("Disconnect rejected");
                ctx?.emit("integration_connection_changed", { provider });
                return { ok: true, provider, outcome: "disconnected", message: `${name} disconnected successfully.` };
            } catch {
                return {
                    ok: false,
                    provider,
                    outcome: "failed",
                    message: `Could not disconnect ${name}. Ask the user to try again.`
                };
            }
        }
    }) as unknown as ChatTool;
}
